import { useState, FormEvent } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEO, { createWebPageSchema, createBreadcrumbSchema, createJobPostingSchema, createOrganizationSchema } from '../../components/feature/SEO';
import WhatsAppButton from '../../components/base/WhatsAppButton';
import { useTranslation } from 'react-i18next';
import { getImageUrl } from '../../utils/siteSettings';

const JOIN_HERO_DEFAULT = 'https://public.readdy.ai/ai/img_res/e5e6162fffa1fb8408c5b1df51d1cafe.jpg';

const Join = () => {
  const { t } = useTranslation();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://unqcode.com';
  const [selectedRole, setSelectedRole] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [chars, setChars] = useState(0);

  const openings = [
    { roleKey: 'join.o1role', teamKey: 'join.o1team', type: 'fulltime', location: 'remote', descKey: 'join.o1desc' },
    { roleKey: 'join.o2role', teamKey: 'join.o2team', type: 'fulltime', location: 'remote', descKey: 'join.o2desc' },
    { roleKey: 'join.o3role', teamKey: 'join.o3team', type: 'fulltime', location: 'hybrid', descKey: 'join.o3desc' },
    { roleKey: 'join.o4role', teamKey: 'join.o4team', type: 'fulltime', location: 'remote', descKey: 'join.o4desc' },
    { roleKey: 'join.o5role', teamKey: 'join.o5team', type: 'fulltime', location: 'remote', descKey: 'join.o5desc' },
    { roleKey: 'join.o6role', teamKey: 'join.o6team', type: 'fulltime', location: 'hybrid', descKey: 'join.o6desc' },
  ];

  const perks = [
    { icon: 'ri-home-office-line', titleKey: 'join.p1title', descKey: 'join.p1desc' },
    { icon: 'ri-money-dollar-circle-line', titleKey: 'join.p2title', descKey: 'join.p2desc' },
    { icon: 'ri-graduation-cap-line', titleKey: 'join.p3title', descKey: 'join.p3desc' },
    { icon: 'ri-heart-line', titleKey: 'join.p4title', descKey: 'join.p4desc' },
    { icon: 'ri-time-line', titleKey: 'join.p5title', descKey: 'join.p5desc' },
    { icon: 'ri-trophy-line', titleKey: 'join.p6title', descKey: 'join.p6desc' },
  ];

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (chars > 500) return;
    setStatus('sending');
    const form = e.currentTarget;
    const data = new URLSearchParams();
    new FormData(form).forEach((value, key) => {
      if (typeof value === 'string') data.append(key, value);
    });
    try {
      const res = await fetch('https://readdy.ai/api/form/d71gup8hb0rqicn68hi0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) { setStatus('sent'); form.reset(); setChars(0); setSelectedRole(''); }
      else setStatus('error');
    } catch { setStatus('error'); }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SEO
        title={t('seo.joinTitle')}
        description={t('seo.joinDesc')}
        keywords="careers Unique Code, jobs Riyadh, tech jobs Saudi Arabia, web developer jobs, mobile developer jobs, وظائف تقنية, وظائف الرياض, العمل في الكود المميز"
        canonical={`${siteUrl}/join`}
        ogType="website"
        schema={{
          '@context': 'https://schema.org',
          '@graph': [
            createWebPageSchema(
              t('seo.joinTitle', 'Join Our Team - Careers at Unique Code | وظائف الكود المميز'),
              t('seo.joinDesc', 'Join Unique Code team. Open positions in web development, mobile apps, AI, design, and marketing. Remote and hybrid work available.'),
              '/join'
            ),
            createOrganizationSchema(),
            createBreadcrumbSchema([
              { name: 'Home', url: '/' },
              { name: 'Join Our Team', url: '/join' },
            ]),
            createJobPostingSchema([
              {
                title: 'Senior Frontend Developer',
                description: 'Build exceptional web experiences with React, TypeScript, and modern frontend tooling.',
                employmentType: 'FULL_TIME',
                workLocation: 'TELECOMMUTE',
              },
              {
                title: 'UI/UX Designer',
                description: 'Craft user-centered interfaces for web and mobile products across diverse client projects.',
                employmentType: 'FULL_TIME',
                workLocation: 'TELECOMMUTE',
              },
              {
                title: 'Mobile App Developer',
                description: 'Develop high-quality iOS and Android apps using React Native and native technologies.',
                employmentType: 'FULL_TIME',
                workLocation: 'TELECOMMUTE',
              },
              {
                title: 'AI/ML Engineer',
                description: 'Build intelligent systems and machine learning models that solve real business problems.',
                employmentType: 'FULL_TIME',
                workLocation: 'TELECOMMUTE',
              },
              {
                title: 'Digital Marketing Specialist',
                description: 'Run data-driven campaigns across paid, organic, and social channels for 30+ active clients.',
                employmentType: 'FULL_TIME',
                workLocation: 'TELECOMMUTE',
              },
              {
                title: 'Sales Executive',
                description: 'Drive new business across GCC and international markets with our premium tech portfolio.',
                employmentType: 'FULL_TIME',
                workLocation: 'TELECOMMUTE',
              },
            ]),
          ],
        }}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-24 bg-gray-900 overflow-hidden">
        <div className="absolute inset-0">
          <img src={getImageUrl('hero-join', JOIN_HERO_DEFAULT)} alt="Join AlCode AlMumaiz" className="w-full h-full object-cover object-top opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-gray-900/80 to-gray-900"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-brand-red/20 border border-brand-red/30 rounded-full px-4 py-2 mb-8">
              <span className="w-2 h-2 bg-brand-red rounded-full animate-pulse"></span>
              <span className="text-white/80 text-sm font-medium">{t('join.heroBadge', { count: openings.length })}</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-black text-white leading-tight mb-6">
              {t('join.heroTitle1')}
              <span className="block text-brand-red">{t('join.heroTitle2')}</span>
            </h1>
            <p className="text-gray-400 text-lg leading-relaxed max-w-xl mx-auto">{t('join.heroDesc')}</p>
          </div>
        </div>
      </section>

      {/* Perks */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-14">
            <p className="text-brand-red font-semibold text-sm uppercase tracking-widest mb-3">{t('join.perksLabel')}</p>
            <h2 className="text-4xl font-black text-gray-900">{t('join.perksTitle')}</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {perks.map((p) => (
              <div key={p.titleKey} className="bg-white rounded-2xl p-7 border border-gray-100 hover:border-brand-red/30 hover:-translate-y-1 transition-all duration-300 group">
                <div className="w-12 h-12 flex items-center justify-center bg-brand-red/10 group-hover:bg-brand-red rounded-xl mb-5 transition-colors duration-300">
                  <i className={`${p.icon} text-brand-red group-hover:text-white text-xl transition-colors duration-300`}></i>
                </div>
                <h3 className="font-bold text-gray-900 text-lg mb-2">{t(p.titleKey)}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{t(p.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-24 bg-gray-50/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-14">
            <p className="text-brand-red font-semibold text-sm uppercase tracking-widest mb-3">{t('join.openingsLabel')}</p>
            <h2 className="text-4xl font-black text-gray-900">{t('join.openingsTitle')}</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {openings.map((job) => {
              const roleLabel = t(job.roleKey);
              return (
                <div key={job.roleKey} className={`bg-white rounded-2xl p-7 border-2 transition-all duration-200 cursor-pointer hover:-translate-y-0.5 ${selectedRole === roleLabel ? 'border-brand-red' : 'border-gray-100 hover:border-brand-red/30'}`} onClick={() => setSelectedRole(roleLabel)}>
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div>
                      <h3 className="font-bold text-gray-900 text-lg">{roleLabel}</h3>
                      <p className="text-brand-red text-sm font-semibold mt-0.5">{t(job.teamKey)}</p>
                    </div>
                    {selectedRole === roleLabel && (
                      <div className="w-6 h-6 flex items-center justify-center bg-brand-red rounded-full flex-shrink-0 mt-1">
                        <i className="ri-check-line text-white text-xs"></i>
                      </div>
                    )}
                  </div>
                  <p className="text-gray-500 text-sm leading-relaxed mb-4">{t(job.descKey)}</p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs rounded-full font-medium">{t(`join.${job.type}`)}</span>
                    <span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs rounded-full font-medium flex items-center gap-1">
                      <i className="ri-map-pin-line text-xs"></i> {t(`join.${job.location}`)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-24 bg-gray-900">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <p className="text-brand-red font-semibold text-sm uppercase tracking-widest mb-3">{t('join.applyLabel')}</p>
            <h2 className="text-4xl font-black text-white">{t('join.applyTitle')}</h2>
            <p className="text-gray-400 mt-3 text-base">
              {selectedRole ? t('join.applySelected', { role: selectedRole }) : t('join.applyDefault')}
            </p>
          </div>
          {status === 'sent' ? (
            <div className="text-center py-16 bg-gray-800 rounded-3xl">
              <div className="w-20 h-20 flex items-center justify-center bg-brand-red/20 border-2 border-brand-red rounded-full mx-auto mb-6">
                <i className="ri-check-double-line text-brand-red text-3xl"></i>
              </div>
              <h3 className="text-2xl font-black text-white mb-3">{t('join.successTitle')}</h3>
              <p className="text-gray-400 text-base max-w-sm mx-auto">{t('join.successDesc')}</p>
              <button onClick={() => setStatus('idle')} className="mt-8 px-6 py-3 bg-brand-red text-white font-bold rounded-xl hover:bg-brand-red-dark transition-colors cursor-pointer whitespace-nowrap">{t('join.successBtn')}</button>
            </div>
          ) : (
            <form data-readdy-form onSubmit={handleSubmit} className="bg-gray-800 rounded-3xl p-8 space-y-5">
              <input type="hidden" name="position" value={selectedRole || 'General Application'} />
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-gray-300 text-sm font-semibold mb-2">{t('join.nameLabel')} *</label>
                  <input name="full_name" required type="text" className="w-full bg-gray-700 border border-gray-600 text-white rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-brand-red transition-colors placeholder-gray-500" />
                </div>
                <div>
                  <label className="block text-gray-300 text-sm font-semibold mb-2">{t('join.emailLabel')} *</label>
                  <input name="email" required type="email" className="w-full bg-gray-700 border border-gray-600 text-white rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-brand-red transition-colors placeholder-gray-500" />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-gray-300 text-sm font-semibold mb-2">{t('join.phoneLabel')}</label>
                  <input name="phone" type="tel" className="w-full bg-gray-700 border border-gray-600 text-white rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-brand-red transition-colors placeholder-gray-500" />
                </div>
                <div>
                  <label className="block text-gray-300 text-sm font-semibold mb-2">{t('join.expLabel')} *</label>
                  <select name="experience" required className="w-full bg-gray-700 border border-gray-600 text-white rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-brand-red transition-colors cursor-pointer">
                    <option value="">Select level</option>
                    <option value="0-1 years">0–1 years</option>
                    <option value="2-3 years">2–3 years</option>
                    <option value="4-6 years">4–6 years</option>
                    <option value="7+ years">7+ years</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-gray-300 text-sm font-semibold mb-2">{t('join.roleLabel')} *</label>
                <select name="role" required value={selectedRole} onChange={(e) => setSelectedRole(e.target.value)} className="w-full bg-gray-700 border border-gray-600 text-white rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-brand-red transition-colors cursor-pointer">
                  <option value="">{t('join.selectPosition')}</option>
                  {openings.map((o) => <option key={o.roleKey} value={t(o.roleKey)}>{t(o.roleKey)}</option>)}
                  <option value="General Application">{t('join.generalApp')}</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-300 text-sm font-semibold mb-2">{t('join.portfolioLabel')}</label>
                <input name="portfolio_url" type="url" placeholder="https://yourportfolio.com" className="w-full bg-gray-700 border border-gray-600 text-white rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-brand-red transition-colors placeholder-gray-500" />
              </div>

              {/* CV Link Field */}
              <div>
                <label className="block text-gray-300 text-sm font-semibold mb-2">
                  {t('join.cvLabel')}
                  <span className="ms-2 text-xs font-normal text-brand-red/80">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 start-0 ps-4 flex items-center pointer-events-none">
                    <i className="ri-file-user-line text-gray-400 text-base"></i>
                  </div>
                  <input
                    name="cv_link"
                    type="url"
                    required
                    placeholder={t('join.cvPlaceholder')}
                    className="w-full bg-gray-700 border border-gray-600 text-white rounded-xl ps-11 pe-4 py-3 text-sm focus:outline-none focus:border-brand-red transition-colors placeholder-gray-500"
                  />
                </div>
                <p className="mt-2 text-xs text-gray-500 flex items-center gap-1.5">
                  <i className="ri-information-line text-brand-red/70"></i>
                  {t('join.cvNote')}
                </p>
              </div>

              <div>
                <label className="block text-gray-300 text-sm font-semibold mb-2">
                  {t('join.aboutLabel')} *
                  <span className={`ms-2 font-normal text-xs ${chars > 500 ? 'text-red-400' : 'text-gray-500'}`}>{chars}/500</span>
                </label>
                <textarea name="message" required rows={5} maxLength={500} onChange={(e) => setChars(e.target.value.length)} className="w-full bg-gray-700 border border-gray-600 text-white rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-brand-red transition-colors placeholder-gray-500 resize-none"></textarea>
              </div>
              {status === 'error' && <p className="text-red-400 text-sm text-center">{t('join.errorText')}</p>}
              <button type="submit" disabled={status === 'sending' || chars > 500} className="w-full py-4 bg-brand-red hover:bg-brand-red-dark text-white font-bold rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                {status === 'sending' ? <><i className="ri-loader-4-line animate-spin"></i> {t('join.submittingText')}</> : <><i className="ri-send-plane-line"></i> {t('join.submitBtn')}</>}
              </button>
            </form>
          )}
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default Join;
